import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import { useSSEStore } from '../../../stores/sseStore';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination } from '../../../components/shared/Pagination';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { EntityLink } from '../../../components/shared/EntityLink';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { DRAFT_STATUS_MAP } from '../shared';
import { StatCard } from '../components/StatCard';
import { ScoreBadge } from '../components/ScoreBadge';
import { DraftDetailModal } from '../modals/DraftDetailModal';
import { ReviewGroupsView } from '../components/ReviewGroupsView';
import { useCan, Can } from '../../../lib/rbac';

function DraftsTab() {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const canReview = can('publisher.drafts_review');
  const canPublish = can('publisher.publish');
  const canEnqueue = can('publisher.enqueue');
  const canDelete = can('publisher.delete');
  const canBulk = canReview || canPublish || canEnqueue || canDelete;
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reasonFilters, setReasonFilters] = useState<Set<string>>(new Set());
  const [reasonMatchMode, setReasonMatchMode] = useState<'any' | 'all' | 'exclusive'>('any');
  const [needsReviewFilter, setNeedsReviewFilter] = useState(false);
  const [noReviewFilter, setNoReviewFilter] = useState(false);
  const pag = usePagination('pub-drafts');
  const [selectedDraft, setSelectedDraft] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'list' | 'groups'>('list');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (needsReviewFilter) params.set('needs_review', 'true');
      if (noReviewFilter) params.set('needs_review', 'false');
      if (reasonFilters.size > 0) {
        params.set('review_reasons', Array.from(reasonFilters).join(','));
        params.set('reason_match', reasonMatchMode);
      }
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any>(`/publisher/drafts?${params}`),
        api.get<any>(
          `/publisher/drafts/stats?seller_id=${sellerId}${statusFilter !== 'all' ? `&status=${encodeURIComponent(statusFilter)}` : ''}`
        ),
      ]);
      if (dataRes.success && dataRes.data) {
        const items = Array.isArray(dataRes.data) ? dataRes.data : dataRes.data.items || [];
        setData(items);
        pag.setTotal((dataRes as any).meta?.total || dataRes.data?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, reasonFilters, reasonMatchMode, needsReviewFilter, noReviewFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // SSE real-time updates (V1 parity)
  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const inlineStatus = (evt: any) => {
      const items = dataRef.current;
      const idx = items.findIndex((i: any) => i.id === evt.draft_id);
      if (idx >= 0) {
        const updated = [...items];
        updated[idx] = { ...updated[idx], status: evt.status || updated[idx].status };
        setData(updated);
      }
    };
    const unsubs = [
      subscribe('draft:created', refresh),
      subscribe('draft:published', refresh),
      subscribe('draft:publish_error', (evt: any) => { inlineStatus({ ...evt, status: 'draft' }); }),
      subscribe('draft:updated', inlineStatus),
      subscribe('draft:status', inlineStatus),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(d => d.id)));
    }
  };

  const bulkAction = async (action: 'approve' | 'reject' | 'publish' | 'delete' | 'reenrich') => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedIds);
      if (action === 'publish') {
        await api.post('/publisher/drafts/publish', { ids, seller_id: sellerId });
      } else if (action === 'delete') {
        await api.post('/publisher/drafts/bulk-delete', { ids });
      } else if (action === 'approve') {
        await api.post('/publisher/drafts/bulk-approve', { ids });
      } else if (action === 'reject') {
        await api.post('/publisher/drafts/bulk-reject', { ids });
      } else if (action === 'reenrich') {
        await api.post('/publisher/drafts/reenrich', { ids, seller_id: sellerId });
      }
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      console.error(`Bulk ${action} failed:`, err);
    } finally {
      setBulkLoading(false);
    }
  };

  // Estados que existen de verdad en la base (dueño 2026-11-01): uno nuevo aparece solo.
  const statuses = [
    'all',
    ...Object.keys(stats?.by_status || {}).sort(
      (a, b) => (stats?.by_status?.[b] ?? 0) - (stats?.by_status?.[a] ?? 0)
    ),
  ];
  if (statusFilter !== 'all' && !statuses.includes(statusFilter)) statuses.push(statusFilter);

  return (
    <div className="space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label={t('ai.totalDrafts')} value={stats.total || 0} icon={<FileText size={16} />} color="blue" />
          <StatCard label={t('pub.needsReview')} value={stats.needs_review || 0} icon={<Eye size={16} />} color="yellow" />
          <StatCard label={t('pub.avgScore')} value={stats.avg_score ? Math.round(stats.avg_score) : '—'} icon={<BarChart3 size={16} />} color="purple" />
          <StatCard label={t('pub.published24h')} value={stats.published_24h || 0} icon={<Rocket size={16} />} color="green" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} ASIN, ${t('common.name').toLowerCase()}...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        {/* Vista lista | grupos (revisión de categoría por lotes) */}
        <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-xs">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'}`}
          >
            Lista
          </button>
          {canReview && (
          <button
            onClick={() => setViewMode('groups')}
            className={`px-3 py-2 transition-colors ${viewMode === 'groups' ? 'bg-blue-600 text-white' : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'}`}
            title="Borradores en revisión por categoría, agrupados por marca + categoría propuesta"
          >
            Revisar por grupos
          </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setReasonFilters(new Set()); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>
              {s === 'all' ? t('ai.allStatuses') : s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        {stats?.review_reason_counts && Object.keys(stats.review_reason_counts).length > 0 && (
          <>
            {/* Selector de razones (agrega al set, no reemplaza) */}
            <select
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (!val) return;
                setReasonFilters((prev) => {
                  const next = new Set(prev);
                  next.add(val);
                  return next;
                });
                pag.resetPage();
              }}
              className={`px-3 py-2 border rounded-lg text-sm transition-colors ${
                reasonFilters.size > 0
                  ? 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text)] border-[var(--color-border)]'
              }`}
            >
              <option value="">+ Agregar razón</option>
              {Object.entries(stats.review_reason_counts)
                .sort(([, a]: any, [, b]: any) => b - a)
                .filter(([reason]) => !reasonFilters.has(reason))
                .map(([reason, cnt]: [string, any]) => (
                  <option key={reason} value={reason}>
                    {reason.replace(/_/g, ' ')} ({cnt})
                  </option>
                ))}
            </select>
            {/* Chips de razones seleccionadas */}
            {Array.from(reasonFilters).map((reason) => (
              <span
                key={reason}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700"
              >
                {reason.replace(/_/g, ' ')}
                <button
                  onClick={() => {
                    setReasonFilters((prev) => {
                      const next = new Set(prev);
                      next.delete(reason);
                      return next;
                    });
                    pag.resetPage();
                  }}
                  className="hover:text-orange-900 dark:hover:text-orange-200"
                  aria-label={`Quitar ${reason}`}
                >
                  ×
                </button>
              </span>
            ))}
            {/* Toggle modo: aparece con 1+ razones. "Solo estos" es útil incluso con 1 chip. */}
            {reasonFilters.size >= 1 && (
              <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden text-xs">
                <button
                  onClick={() => { setReasonMatchMode('any'); pag.resetPage(); }}
                  className={`px-2.5 py-1.5 transition-colors ${
                    reasonMatchMode === 'any'
                      ? 'bg-orange-500 text-white'
                      : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
                  }`}
                  title="Drafts que tengan al menos uno de los motivos seleccionados (puede tener otros)"
                >
                  Cualquiera
                </button>
                {reasonFilters.size >= 2 && (
                  <button
                    onClick={() => { setReasonMatchMode('all'); pag.resetPage(); }}
                    className={`px-2.5 py-1.5 transition-colors border-l border-[var(--color-border)] ${
                      reasonMatchMode === 'all'
                        ? 'bg-orange-500 text-white'
                        : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
                    }`}
                    title="Drafts que tengan todos los motivos seleccionados (puede tener otros)"
                  >
                    Todas
                  </button>
                )}
                <button
                  onClick={() => { setReasonMatchMode('exclusive'); pag.resetPage(); }}
                  className={`px-2.5 py-1.5 transition-colors border-l border-[var(--color-border)] ${
                    reasonMatchMode === 'exclusive'
                      ? 'bg-orange-500 text-white'
                      : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]'
                  }`}
                  title="Drafts que tengan EXACTAMENTE estos motivos, ninguno más"
                >
                  Solo estos
                </button>
              </div>
            )}
          </>
        )}
        <button
          onClick={() => {
            setNeedsReviewFilter(!needsReviewFilter);
            if (!needsReviewFilter) setNoReviewFilter(false);
            pag.resetPage();
          }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
            needsReviewFilter
              ? 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700'
              : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]'
          }`}
        >
          <Eye size={14} />
          {t('pub.needsReview')}
          {stats?.needs_review > 0 && (
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              needsReviewFilter ? 'bg-white/40 text-yellow-800' : 'bg-yellow-600 text-white'
            }`}>{stats.needs_review}</span>
          )}
        </button>
        <button
          onClick={() => {
            setNoReviewFilter(!noReviewFilter);
            if (!noReviewFilter) setNeedsReviewFilter(false);
            pag.resetPage();
          }}
          title="Drafts sin motivo de revisión (listos para publicar)"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
            noReviewFilter
              ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700'
              : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]'
          }`}
        >
          <Rocket size={14} />
          Listos
          {stats?.no_review > 0 && (
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              noReviewFilter ? 'bg-white/40 text-emerald-800' : 'bg-emerald-600 text-white'
            }`}>{stats.no_review}</span>
          )}
        </button>
      </div>

      {/* Vista por grupos: revisión de categoría en lotes */}
      {viewMode === 'groups' && (
        <ReviewGroupsView sellerId={sellerId} onResolved={loadData} />
      )}

      {/* Draft Table */}
      {viewMode === 'list' && (
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-3 py-3">
                {canBulk && (
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedIds.size === data.length}
                    onChange={toggleAll}
                    className="rounded border-[var(--color-border)]"
                  />
                )}
              </th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">Img</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">ASIN / {t('common.name')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.category')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.score')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('pub.needsReview')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.price')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('pub.noDrafts')}</td></tr>
            ) : data.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedDraft(row)}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  {canBulk && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="rounded border-[var(--color-border)]"
                    />
                  )}
                </td>
                <td className="px-3 py-3">
                  {row.product_image || row.thumbnail ? (
                    <img src={row.product_image || row.thumbnail} alt="" className="w-[50px] h-[50px] rounded object-cover" />
                  ) : (
                    <div className="w-[50px] h-[50px] rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                      <Package size={18} className="text-[var(--color-text-tertiary)]" />
                    </div>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="min-w-0">
                    {row.asin && <EntityLink type="asin" value={row.asin} />}
                    <span className="block text-[var(--color-text)] text-xs mt-0.5 max-w-[280px]" title={row.title_seo || row.title}>
                      {row.title_seo || row.title || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs max-w-[150px]" title={row.ml_category_name || row.category}>
                  {row.ml_category_name || row.category || '—'}
                </td>
                <td className="px-3 py-3 text-center">
                  <ScoreBadge score={row.validation_score} />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={DRAFT_STATUS_MAP[row.status] || 'unknown'} label={row.status?.replace(/_/g, ' ')} />
                </td>
                <td className="px-3 py-3 text-center">
                  {row.needs_review && row.review_reasons?.length > 0 ? (
                    <span
                      className="inline-block px-1.5 py-0.5 bg-amber-500 text-black dark:bg-amber-400 dark:text-black rounded text-[10px] font-semibold leading-tight whitespace-nowrap"
                      title={row.review_reasons.length > 1 ? row.review_reasons.slice(1).join(' • ') : undefined}
                    >
                      {row.review_reasons[0].replace(/_/g, ' ')}
                    </span>
                  ) : row.needs_review ? (
                    <AlertCircle size={16} className="mx-auto text-yellow-500" />
                  ) : null}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text)]">
                  {canMoney && row.price ? `$${Number(row.price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {viewMode === 'list' && (
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && canBulk && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-xl shadow-xl px-6 py-3 flex items-center gap-4">
          <span className="text-sm text-[var(--color-text)] font-medium">
            {selectedIds.size} {t('pub.selected')}
          </span>
          <div className="flex items-center gap-2">
            <Can cap="publisher.drafts_review">
            <button
              onClick={() => bulkAction('approve')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              <Check size={14} />
              {t('pub.approve')}
            </button>
            <button
              onClick={() => bulkAction('reject')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              <Ban size={14} />
              {t('pub.reject')}
            </button>
            </Can>
            <Can cap="publisher.publish">
            <button
              onClick={() => bulkAction('publish')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {bulkLoading ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
              {t('pub.publish')}
            </button>
            </Can>
            <Can cap="publisher.enqueue">
            <button
              onClick={() => bulkAction('reenrich')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              title="Re-run enrichment for selected drafts"
            >
              <Sparkles size={14} />
              Re-Enrich
            </button>
            </Can>
            <Can cap="publisher.delete">
            <button
              onClick={() => bulkAction('delete')}
              disabled={bulkLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 disabled:opacity-50"
            >
              <Trash2 size={14} />
              {t('common.delete')}
            </button>
            </Can>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1 rounded hover:bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Draft Detail Modal */}
      {selectedDraft && (
        <DraftDetailModal
          draft={selectedDraft}
          onClose={() => setSelectedDraft(null)}
          onUpdate={loadData}
          sellerId={sellerId}
        />
      )}
    </div>
  );
}

export { DraftsTab };
